import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronDown } from 'lucide-react';

export interface CrumbSibling {
  label: string;
  to: string;
}

export interface Crumb {
  label: ReactNode;
  to?: string;
  siblings?: CrumbSibling[];
}

export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center text-sm text-white/50 gap-1 flex-wrap min-w-0"
    >
      {items.map((c, i) => {
        const last = i === items.length - 1;
        return (
          <span key={i} className="inline-flex items-center gap-1 min-w-0">
            {c.to && !last ? (
              <Link
                to={c.to}
                className="hover:text-white/90 truncate max-w-[180px] transition-colors"
                title={typeof c.label === 'string' ? c.label : undefined}
              >
                {c.label}
              </Link>
            ) : (
              <span
                className={`truncate max-w-[260px] ${last ? 'text-white font-medium' : ''}`}
                title={typeof c.label === 'string' ? c.label : undefined}
              >
                {c.label}
              </span>
            )}
            {!last && (
              c.siblings && c.siblings.length > 0 ? (
                <SiblingChevron siblings={c.siblings} />
              ) : (
                <ChevronRight size={14} className="text-white/25 shrink-0" />
              )
            )}
          </span>
        );
      })}
    </nav>
  );
}

function SiblingChevron({ siblings }: { siblings: CrumbSibling[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current || !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-white/25 hover:text-white/60 p-0.5 transition-colors"
        aria-label="Show siblings"
        aria-expanded={open}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-30 min-w-[180px] max-w-[280px] bg-white border border-[#E3E3F1] rounded-lg shadow-[0_8px_20px_rgba(15,23,42,0.10)] py-1"
          role="menu"
        >
          {siblings.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              onClick={() => setOpen(false)}
              className="block px-3 py-1.5 text-sm text-[#12122C] hover:bg-[#EFF0FF] hover:text-[#0410BD] truncate transition-colors"
              role="menuitem"
              title={s.label}
            >
              {s.label}
            </Link>
          ))}
        </div>
      )}
    </span>
  );
}
